import type { Graphics } from 'pixi.js';
import { PAPER, lerpColor } from '../../color';
import type { Body, Kit, WorldDress, WorldFx } from '../../world';

const TAU = Math.PI * 2;
const GROOVES = [0.88, 0.7, 0.52];
const FACETS = [
  -0.42, -0.52, 0.2, 0.15, 1, -0.13, -0.57, 0.23, 0.14, 0, 0.18, -0.5, 0.17, 0.16, 1,
  -0.62, -0.05, 0.19, 0.18, 0, -0.31, -0.02, 0.23, 0.16, 1, 0.03, 0.01, 0.19, 0.19, 0, 0.35, -0.04, 0.22, 0.15, 1,
  -0.4, 0.45, 0.21, 0.15, 0, -0.08, 0.49, 0.22, 0.16, 1, 0.25, 0.42, 0.17, 0.15, 0,
];
const TILES = 12;
const MILESTONE_GLINTS = 16;

function diamond(g: Graphics, x: number, y: number, s: number, rot: number): void {
  const c = Math.cos(rot) * s;
  const d = Math.sin(rot) * s;
  g.poly([x + c, y + d, x - d, y + c, x - c, y - d, x + d, y - c], true);
}

function facets(k: Kit, body: Body, tone: number, color: number, alpha: number): void {
  const r = body.radius;
  for (let i = 0; i < FACETS.length; i += 5) {
    if (FACETS[i + 4] !== tone) continue;
    const w = FACETS[i + 2]! * r * body.scaleX;
    const h = FACETS[i + 3]! * r * body.scaleY;
    const x = body.x + FACETS[i]! * r * body.scaleX - w / 2;
    const y = body.y + FACETS[i + 1]! * r * body.scaleY - h / 2;
    k.g.roundRect(x, y, w, h, Math.min(w, h) * 0.3);
  }
  k.g.fill({ color, alpha });
}

export const dress: WorldDress = {
  incomingCircle(k, body, color, alpha) {
    const { g, pal } = k;
    const r = body.radius;
    for (const f of GROOVES) g.ellipse(body.x, body.y, r * f * body.scaleX, r * f * body.scaleY);
    g.stroke({ width: Math.max(1, r * 0.05), color: lerpColor(color, pal.ink, 0.45), alpha: 0.45 * alpha });
    g.ellipse(body.x, body.y, r * 0.34 * body.scaleX, r * 0.34 * body.scaleY).fill({ color: pal.spot, alpha: 0.85 * alpha });
    g.ellipse(body.x, body.y, r * 0.07 * body.scaleX, r * 0.07 * body.scaleY).fill({ color: pal.ink, alpha: 0.8 * alpha });
    k.rimLight(body, 0.5 * alpha);
  },

  mineCircle(k, body, alpha, width, fill) {
    const { g, pal } = k;
    const r = body.radius;
    k.trace(body);
    g.fill({ color: fill, alpha });
    facets(k, body, 0, lerpColor(fill, pal.heldShade, 0.75), 0.45 * alpha);
    facets(k, body, 1, lerpColor(fill, pal.heldLight, 0.8), 0.6 * alpha);
    k.rimLight(body, 0.55 * alpha);
    g.star(body.x - 0.42 * r * body.scaleX, body.y - 0.46 * r * body.scaleY, 4, 0.26 * r, 0.07 * r, 0).fill({
      color: PAPER,
      alpha: 0.75 * alpha,
    });
    k.trace(body);
    g.stroke({ width: width * 0.8, color: k.outline, alpha, join: 'round' });
  },

  target(k, body, alpha, _spread, reach, spin, edge) {
    const { g, pal } = k;
    k.trace(body);
    g.fill({ color: lerpColor(pal.bg, 0x000000, 0.4), alpha: 0.5 * alpha });
    k.trace(body);
    g.fill({ color: pal.spot, alpha: 0.08 * alpha });
    g.circle(body.x, body.y, reach).stroke({ width: 1.2, color: pal.spot, alpha: 0.45 * alpha });
    const s = Math.max(2.5, k.unit * 0.016);
    for (let tone = 0; tone < 2; tone++) {
      k.ringDots(body, reach, TILES, spin, (x, y, i, a) => {
        if (i % 2 === tone) diamond(g, x, y, s, a + Math.PI / 4);
      });
      g.fill({ color: tone === 0 ? pal.held : pal.incoming.circle, alpha: 0.7 * alpha });
    }
    k.trace(body);
    g.stroke({ width: edge, color: pal.rim, alpha: 0.85 * alpha, join: 'round' });
  },

  pip(k, x, y, radius, on, last, alpha, width) {
    const { g, pal } = k;
    g.circle(x, y, radius * 1.1);
    if (on) g.fill({ color: last ? pal.spot : lerpColor(pal.held, pal.rim, 0.4), alpha });
    g.stroke({ width: width * 0.8, color: last ? pal.spot : pal.rim, alpha: (on ? 0.9 : 0.5) * alpha });
  },

  path(k, points, count, alpha) {
    const { g, pal } = k;
    const step = Math.max(6, k.unit * 0.035);
    for (let i = 2; i < count; i += 2) {
      const x0 = points[i - 2]!;
      const y0 = points[i - 1]!;
      const len = Math.hypot(points[i]! - x0, points[i + 1]! - y0);
      const n = Math.min(80, Math.floor(len / step));
      for (let s = 1; s < n; s++) {
        const t = s / n;
        g.circle(x0 + (points[i]! - x0) * t, y0 + (points[i + 1]! - y0) * t, s % 4 === 0 ? 2 : 1.2);
      }
    }
    g.fill({ color: pal.path, alpha: alpha * 2.6 });
  },
};

export const fx: WorldFx = {
  bit(g, x, y, size, i, color, alpha, rot) {
    if (i % 3 === 0) diamond(g, x, y, size * 0.75, rot);
    else g.star(x, y, 4, size * 1.3, size * 0.32, rot);
    g.fill({ color, alpha });
  },

  milestone(g, m, pal) {
    g.circle(m.x, m.y, m.r).stroke({ width: m.width, color: pal.held, alpha: m.fade * 0.5 });
    g.circle(m.x, m.y, m.r * 1.2).stroke({ width: m.width * 2.2, color: pal.incoming.circle, alpha: m.fade * 0.12 });
    const s = m.unit * 0.018 * (0.7 + 0.3 * m.grow);
    for (let i = 0; i < MILESTONE_GLINTS; i++) {
      const a = (i / MILESTONE_GLINTS) * TAU + m.grow * 0.5;
      const rr = m.r * (i % 2 === 0 ? 1.12 : 1.28);
      const ss = i % 2 === 0 ? s : s * 0.7;
      g.star(m.x + Math.cos(a) * rr, m.y + Math.sin(a) * rr, 4, ss, ss * 0.28, 0);
    }
    g.fill({ color: pal.heldLight, alpha: m.fade * 0.6 });
  },

  trail(g, x, y, size, angle) {
    g.star(x, y, 4, size * 1.2, size * 0.3, angle);
  },
};
